import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { configuracao } from '../configuracao.js';

/**
 * Sessão do operador no Cognito.
 *
 * A biblioteca guarda os tokens no localStorage e renova o access token sozinha a partir
 * do refresh token. Só encapsulamos aqui o que o cockpit precisa: entrar, sair, pegar o
 * token para o header Authorization e trocar a senha temporária no primeiro acesso.
 */
const pool = new CognitoUserPool({
  UserPoolId: configuracao.cognito.poolId,
  ClientId: configuracao.cognito.clienteId,
});

export interface OperadorLogado {
  nome: string;
  email: string;
  papeis: string[];
}

/**
 * Onde o login pode parar antes de entrar.
 *
 * Produção exige MFA por aplicativo autenticador (`MfaConfiguration: ON`, só software
 * token). Isso cria dois desvios que **não** existem em dev, onde o MFA é opcional:
 *
 *  - `cadastrar-mfa` — primeiro acesso. O Cognito devolve um segredo, o operador registra
 *    no aplicativo e digita o código para provar que registrou.
 *  - `codigo-mfa` — todo acesso seguinte.
 *
 * Cada desvio é uma promessa que só resolve quando o operador responde. Sem tratar,
 * `authenticateUser` simplesmente nunca chama nenhum callback e a tela fica girando —
 * era exatamente o que aconteceria em produção antes disto existir.
 */
export type ResultadoDoLogin =
  | { tipo: 'entrou'; operador: OperadorLogado }
  | {
      tipo: 'nova-senha';
      /** Atributos que o pool exige preencher junto com a senha — na prática, o nome. */
      exigeNome: boolean;
      concluir: (novaSenha: string, nome?: string) => Promise<ResultadoDoLogin>;
    }
  | { tipo: 'cadastrar-mfa'; segredo: string; uri: string; concluir: Conclusao }
  | { tipo: 'codigo-mfa'; concluir: Conclusao };

/** Recebe o código de seis dígitos do aplicativo autenticador. */
export type Conclusao = (codigo: string) => Promise<ResultadoDoLogin>;

/**
 * A biblioteca devolve os obrigatórios ora como `name`, ora como `userAttributes.name`.
 * Normalizar aqui evita depender de qual das duas formas veio.
 */
function exigeAtributo(obrigatorios: string[] | undefined, atributo: string): boolean {
  return (obrigatorios ?? []).some((a) => a.replace(/^userAttributes\./, '') === atributo);
}

const APLICATIVO = 'A Ponte · Cockpit';

/** URI que o aplicativo autenticador entende, para virar QR Code na tela. */
function uriDoAutenticador(email: string, segredo: string): string {
  const conta = encodeURIComponent(`${APLICATIVO}:${email}`);
  const emissor = encodeURIComponent(APLICATIVO);
  return `otpauth://totp/${conta}?secret=${segredo}&issuer=${emissor}`;
}

function operadorDaSessao(sessao: CognitoUserSession): OperadorLogado {
  const claims = sessao.getIdToken().payload as Record<string, unknown>;
  const grupos = claims['cognito:groups'];

  return {
    nome: String(claims.name ?? claims.email ?? ''),
    email: String(claims.email ?? ''),
    papeis: Array.isArray(grupos) ? grupos.map(String) : [],
  };
}

/**
 * Callbacks do fluxo de autenticação.
 *
 * São recursivos porque o Cognito encadeia desafios: senha temporária → nova senha →
 * cadastro de MFA → entrou. Cada resposta pode abrir o desafio seguinte, e é o mesmo
 * conjunto de callbacks que atende todos eles. Tratar só o primeiro deixaria a promessa
 * pendurada para sempre no segundo.
 */
function callbacksDoLogin(
  usuario: CognitoUser,
  email: string,
  resolver: (resultado: ResultadoDoLogin) => void,
  rejeitar: (erro: Error) => void,
) {
  const callbacks = {
    onSuccess: (sessao: CognitoUserSession) =>
      resolver({ tipo: 'entrou', operador: operadorDaSessao(sessao) }),

    onFailure: (erro: Error) => rejeitar(traduzirErro(erro)),

    /**
     * Senha temporária: o operador define a definitiva.
     *
     * O pool pede `name` como atributo obrigatório. Se o operador foi criado sem ele —
     * um `admin-create-user` sem `Name=name` — o Cognito recusa a troca com "Invalid
     * attributes given, name is missing", em inglês e sem saída: o primeiro acesso fica
     * impossível e a senha temporária expira em três dias. Perguntar o nome aqui resolve
     * na hora, e ainda é a pessoa certa respondendo qual é o próprio nome.
     */
    newPasswordRequired: (_atributos: unknown, obrigatorios: string[]) => {
      resolver({
        tipo: 'nova-senha',
        exigeNome: exigeAtributo(obrigatorios, 'name'),
        concluir: (novaSenha: string, nome?: string) =>
          new Promise<ResultadoDoLogin>((ok, falha) => {
            // Só os obrigatórios que faltam: o Cognito recusa reenviar imutáveis, como o e-mail.
            const atributos = nome?.trim() ? { name: nome.trim() } : {};
            usuario.completeNewPasswordChallenge(
              novaSenha,
              atributos,
              callbacksDoLogin(usuario, email, ok, falha),
            );
          }),
      });
    },

    /** Primeiro acesso com MFA obrigatório: o Cognito emite o segredo do autenticador. */
    mfaSetup: () => {
      usuario.associateSoftwareToken({
        associateSecretCode: (segredo: string) => {
          resolver({
            tipo: 'cadastrar-mfa',
            segredo,
            uri: uriDoAutenticador(email, segredo),
            concluir: (codigo: string) =>
              new Promise<ResultadoDoLogin>((ok, falha) => {
                usuario.verifySoftwareToken(
                  codigo,
                  APLICATIVO,
                  callbacksDoLogin(usuario, email, ok, falha),
                );
              }),
          });
        },
        onFailure: (erro: Error) => rejeitar(traduzirErro(erro)),
      });
    },

    /** Acessos seguintes: só o código de seis dígitos. */
    totpRequired: () => {
      resolver({
        tipo: 'codigo-mfa',
        concluir: (codigo: string) =>
          new Promise<ResultadoDoLogin>((ok, falha) => {
            usuario.sendMFACode(
              codigo,
              callbacksDoLogin(usuario, email, ok, falha),
              'SOFTWARE_TOKEN_MFA',
            );
          }),
      });
    },

    /**
     * O pool só habilita software token, então não há o que escolher — mas se um dia
     * alguém ligar SMS, sem isto o login para de responder sem dizer por quê.
     */
    selectMFAType: () => {
      usuario.sendMFASelectionAnswer('SOFTWARE_TOKEN_MFA', callbacks);
    },
  };

  return callbacks;
}

export function entrar(email: string, senha: string): Promise<ResultadoDoLogin> {
  const usuario = new CognitoUser({ Username: email, Pool: pool });
  const credenciais = new AuthenticationDetails({ Username: email, Password: senha });

  return new Promise((resolver, rejeitar) => {
    usuario.authenticateUser(credenciais, callbacksDoLogin(usuario, email, resolver, rejeitar));
  });
}

export function sair(): void {
  pool.getCurrentUser()?.signOut();
}

// ─── Senha esquecida ─────────────────────────────────────────────────────────

/**
 * Pede o código de recuperação por e-mail.
 *
 * Devolve o destino já mascarado pelo próprio Cognito ("a***@r***.com.br"), que é o que
 * a tela mostra: confirma para a pessoa certa qual caixa conferir, sem revelar o endereço
 * completo a quem estiver olhando a tela por cima do ombro.
 *
 * O pool sobe com `preventUserExistenceErrors`, então e-mail inexistente recebe a mesma
 * resposta de um existente — não dá para descobrir quem tem acesso ao cockpit por
 * tentativa e erro.
 */
export function pedirCodigoDeRecuperacao(email: string): Promise<string> {
  const usuario = new CognitoUser({ Username: email, Pool: pool });

  return new Promise((resolver, rejeitar) => {
    usuario.forgotPassword({
      inputVerificationCode: (dados: unknown) => {
        const destino = (dados as { CodeDeliveryDetails?: { Destination?: string } })
          ?.CodeDeliveryDetails?.Destination;
        resolver(destino ?? email);
      },
      // Chamado quando o pool decide não pedir código. Nada a fazer além de seguir.
      onSuccess: () => resolver(email),
      onFailure: (erro: Error) => rejeitar(traduzirErro(erro)),
    });
  });
}

/** Confirma o código recebido por e-mail e grava a senha nova. */
export function redefinirSenha(email: string, codigo: string, novaSenha: string): Promise<void> {
  const usuario = new CognitoUser({ Username: email, Pool: pool });

  return new Promise((resolver, rejeitar) => {
    usuario.confirmPassword(codigo.trim(), novaSenha, {
      onSuccess: () => resolver(),
      onFailure: (erro: Error) => rejeitar(traduzirErro(erro, { codigoPorEmail: true })),
    });
  });
}

// ─── Troca de senha com a sessão aberta ──────────────────────────────────────

/**
 * Troca a própria senha, exigindo a atual.
 *
 * Exigir a senha atual é o que impede que uma estação destravada e sem dono vire uma
 * conta sequestrada: quem senta na cadeira alheia não consegue trocar a senha sem saber
 * a que está em uso.
 */
export function trocarMinhaSenha(senhaAtual: string, novaSenha: string): Promise<void> {
  const usuario = pool.getCurrentUser();

  return new Promise((resolver, rejeitar) => {
    if (!usuario) {
      rejeitar(new Error('Sua sessão expirou. Entre de novo para trocar a senha.'));
      return;
    }

    // `changePassword` precisa de sessão válida; `getSession` renova pelo refresh token.
    usuario.getSession((erroDaSessao: Error | null, sessao: CognitoUserSession | null) => {
      if (erroDaSessao || !sessao?.isValid()) {
        rejeitar(new Error('Sua sessão expirou. Entre de novo para trocar a senha.'));
        return;
      }

      usuario.changePassword(senhaAtual, novaSenha, (erro?: Error) => {
        if (erro) {
          rejeitar(traduzirErro(erro, { senhaAtualErrada: true }));
          return;
        }
        resolver();
      });
    });
  });
}

/** Sessão válida do operador, ou `null` se não há ninguém logado. */
export function sessaoAtual(): Promise<{ operador: OperadorLogado; token: string } | null> {
  const usuario = pool.getCurrentUser();
  if (!usuario) return Promise.resolve(null);

  return new Promise((resolver) => {
    usuario.getSession((erro: Error | null, sessao: CognitoUserSession | null) => {
      if (erro || !sessao?.isValid()) {
        resolver(null);
        return;
      }
      resolver({
        operador: operadorDaSessao(sessao),
        token: sessao.getIdToken().getJwtToken(),
      });
    });
  });
}

/** Token para o header Authorization. Renova sozinho quando está perto de expirar. */
export async function tokenAtual(): Promise<string | null> {
  return (await sessaoAtual())?.token ?? null;
}

const MENSAGENS: Record<string, string> = {
  NotAuthorizedException: 'E-mail ou senha incorretos.',
  UserNotFoundException: 'E-mail ou senha incorretos.',
  PasswordResetRequiredException: 'Sua senha precisa ser redefinida. Procure a operação.',
  UserNotConfirmedException: 'Cadastro ainda não confirmado. Procure a operação.',
  TooManyRequestsException: 'Muitas tentativas seguidas. Aguarde um minuto e tente de novo.',
  LimitExceededException: 'Muitas tentativas seguidas. Aguarde um minuto e tente de novo.',
  InvalidPasswordException:
    'A senha precisa ter ao menos 12 caracteres, com maiúscula, minúscula, número e símbolo.',
  CodeMismatchException:
    'Código incorreto. Confira o número no aplicativo — ele troca a cada 30 segundos.',
  ExpiredCodeException: 'O código expirou. Digite o número que está no aplicativo agora.',
  InvalidParameterException:
    'Não é possível recuperar a senha desta conta pelo e-mail. Procure a operação.',
  EnableSoftwareTokenMFAException:
    'Código incorreto. Se o aplicativo mostra um horário diferente do relógio do ' +
    'computador, acerte a hora antes de tentar de novo.',
};

/**
 * O mesmo código do Cognito quer dizer coisas diferentes conforme o que se pediu.
 *
 * `NotAuthorizedException` é o exemplo: no login é senha errada; na troca de senha é a
 * senha **atual** errada; na recuperação costuma ser conta que ainda não teve o primeiro
 * acesso, e nesse caso mandar "e-mail ou senha incorretos" faria a pessoa tentar de novo
 * para sempre.
 */
function traduzirErro(
  erro: Error,
  contexto: { senhaAtualErrada?: boolean; codigoPorEmail?: boolean } = {},
): Error {
  const nome = (erro as Error & { code?: string }).code ?? erro.name;
  const original = erro.message ?? '';

  // Os textos padrão falam do aplicativo autenticador, que é o caso do dia a dia. Aqui o
  // código veio por e-mail, e mandar conferir o autenticador mandaria olhar o lugar errado.
  if (contexto.codigoPorEmail) {
    if (nome === 'CodeMismatchException') {
      return new Error('Código incorreto. Confira o que chegou por e-mail.');
    }
    if (nome === 'ExpiredCodeException') {
      return new Error('O código expirou. Peça um novo para recomeçar.');
    }
  }

  if (nome === 'NotAuthorizedException') {
    if (contexto.senhaAtualErrada) return new Error('A senha atual está incorreta.');
    if (/current state/i.test(original)) {
      return new Error(
        'Esta conta ainda não teve o primeiro acesso, então não há senha a recuperar. ' +
          'Entre com a senha temporária do e-mail de convite, ou peça à operação para reenviá-la.',
      );
    }
  }

  return new Error(MENSAGENS[nome] ?? original ?? 'Não foi possível concluir.');
}
